import React, { useState, type ChangeEvent } from "react";
import {
  Box,
  Button,
  styled,
  type Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  UserProfilesApiDeleteUserProfileImageRequest,
  UserProfilesApiProfileImageRequest,
} from "@rentablo/api/apiv1";
import UploadIcon from "@icons/myicons/cloud-storage-upload.svg?react";
import { useDeleteUserProfileImage, useUserProfile } from "@rentablo/api/apiv1";
import {
  useGetUserProfileImage,
  useUserProfileImageUpdate,
} from "@features/settings/useSettings";
import { createObjectUrl } from "@helpers/files";
import { GET_USER_PROFILE_IMAGE } from "@api/cacheKeys";

const AvatarCircle = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 56,
  height: 56,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  borderRadius: "50%",
  color: theme.palette.button.primaryButton.textOnDefault,
}));

const StyledImage = styled("img")({
  width: 56,
  height: 56,
  borderRadius: "50%",
  objectFit: "cover",
});

export const AvatarUpload = () => {
  const [selectedImage, setSelectedImage] = useState("");

  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  const { data: user, isSuccess: isUserProfileSuccessfullyFetched } =
    useUserProfile();
  const userId = user?.id;

  useGetUserProfileImage({ id: userId } as UserProfilesApiProfileImageRequest, {
    enabled: isUserProfileSuccessfullyFetched && Boolean(userId),
    onSuccess: (data) => {
      if (data?.size > 4) {
        setSelectedImage(createObjectUrl(data));
      }
    },
  });

  const { mutate: updateImage, isPending: isImageUpdating } =
    useUserProfileImageUpdate({
      mutationKey: [selectedImage],
      onSuccess: async () => {
        await queryClient.resetQueries({
          queryKey: [GET_USER_PROFILE_IMAGE, { id: userId }],
        });
      },
    });

  const { mutate: deleteImage } = useDeleteUserProfileImage({
    onSuccess: async () => {
      setSelectedImage("");
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0] && userId) {
      updateImage({ id: userId, image: files[0] });
    }
  };

  const firstNameLetter = user?.firstName?.[0] || "";
  const lastNameLetter = user?.lastName?.[0] || "";
  const name = `${firstNameLetter}${lastNameLetter}`.trim();

  return (
    <Box display="flex" gap={5} alignItems="center" pb={7}>
      <Box>
        {selectedImage && <StyledImage src={selectedImage} />}
        {!selectedImage && (
          <AvatarCircle>
            <Typography
              textTransform="uppercase"
              fontSize="20px"
              fontWeight={600}
            >
              {name}
            </Typography>
          </AvatarCircle>
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body4" color="textSecondary">
          {t("settings.profile.avatar.title")}
        </Typography>
        <Box display="flex" gap={3} flexDirection={isXs ? "column" : "row"}>
          <Button
            component="label"
            variant="primary"
            startIcon={<UploadIcon />}
            disabled={isImageUpdating}
          >
            {selectedImage
              ? t("settings.profile.avatar.replace")
              : t("settings.profile.avatar.upload")}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleChange}
            />
          </Button>
          {selectedImage && (
            <Button
              variant="outlined"
              onClick={() =>
                deleteImage({
                  id: userId,
                } as UserProfilesApiDeleteUserProfileImageRequest)
              }
            >
              {t("settings.profile.avatar.delete")}
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="textSecondary">
          {t("settings.profile.avatar.info")}
        </Typography>
      </Box>
    </Box>
  );
};
